// A seed being made from nothing, kept in this browser until it is saved:
// the room, its title, and the takes recorded into it. IndexedDB, because a
// take is a WAV blob and localStorage holds strings. A reload, a crash or a
// closed tab finds the draft where it was left.
//
// Best effort everywhere: a private window or blocked storage has no
// IndexedDB, and the room then works exactly as before — in memory, guarded
// on leave — rather than failing.

import type { CanonicalRoom } from "@shared/sync/state.js";

export interface RoomDraft {
  id: string;
  title: string;
  room: CanonicalRoom;
  /** The takes the room's clips name, by their `local/…` key. */
  takes: Array<{ key: string; blob: Blob }>;
  updatedAt: number;
}

const DB_NAME = "seedbed";
const STORE = "room-drafts";
/** Older drafts than the newest few are dropped on write. */
const KEEP = 5;

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: "id" });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function done<T>(req: IDBRequest<T>): Promise<T | null> {
  return new Promise((resolve) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

export async function loadDraft(id: string): Promise<RoomDraft | null> {
  const db = await open();
  if (!db) return null;
  try {
    const draft = await done(db.transaction(STORE, "readonly").objectStore(STORE).get(id) as IDBRequest<RoomDraft | undefined>);
    return draft ?? null;
  } finally {
    db.close();
  }
}

/** Every draft, newest first. */
export async function listDrafts(): Promise<RoomDraft[]> {
  const db = await open();
  if (!db) return [];
  try {
    const all = await done(db.transaction(STORE, "readonly").objectStore(STORE).getAll() as IDBRequest<RoomDraft[]>);
    return (all ?? []).sort((a, b) => b.updatedAt - a.updatedAt);
  } finally {
    db.close();
  }
}

export async function saveDraft(draft: RoomDraft): Promise<void> {
  const db = await open();
  if (!db) return;
  try {
    const store = db.transaction(STORE, "readwrite").objectStore(STORE);
    await done(store.put(draft));
    const all = (await done(store.getAll() as IDBRequest<RoomDraft[]>)) ?? [];
    for (const old of all.sort((a, b) => b.updatedAt - a.updatedAt).slice(KEEP)) await done(store.delete(old.id));
  } finally {
    db.close();
  }
}

export async function deleteDraft(id: string): Promise<void> {
  const db = await open();
  if (!db) return;
  try {
    await done(db.transaction(STORE, "readwrite").objectStore(STORE).delete(id));
  } finally {
    db.close();
  }
}

export function mintDraftId(): string {
  return crypto.randomUUID().slice(0, 8);
}
